import { Card, Container, PageContainer, type OptionProps } from '@amelio/ui-library';
import { useAtom, useAtomValue } from 'jotai';

import { MeetingPlanTemplateSection } from './MeetingPlanTemplateSection/MeetingPlanTemplateSection';
import { TemplateHeader } from './TemplateHeader/TemplateHeader';
import { TemplateSection } from './TemplateSection/TemplateSection';
import { TemplateSettings } from './TemplateSetting/TemplateSettings';

import type { PaginatedResponse } from '@/types/api';
import type { ReviewTemplate } from '@/types/review/ReviewTemplate';
import type { EditableMeetingTemplate } from '@/types/thematic-meetings';
import type {
    FetchNextPageOptions,
    InfiniteData,
    InfiniteQueryObserverResult,
} from '@tanstack/react-query';
import type { PropsWithChildren } from 'react';

import {
    is360CollapsedAtom,
    isManagerCollapsedAtom,
    isMeetingPlanCollapsedAtom,
    isSelfCollapsedAtom,
} from '@/atoms/manageTemplates';
import { userInstanceAtom } from '@/atoms/userDetails';
import {
    EvaluationType,
    type EvaluationTypeValue,
} from '@/constants/cycle-evaluation/CycleEvaluation';
import { useAmelioTranslations } from '@/hooks';

/** Everything one review template section needs, so the page declares a section rather than a row of flags. */
export type ReviewTemplateSection = {
    templates: ReviewTemplate[];
    isLoading: boolean;
    isCreatingTemplate: boolean;
    hasMoreTemplates: boolean;
    isFetchingNextPage: boolean;
    onImport: () => void;
    fetchMore: (
        options?: FetchNextPageOptions | undefined,
    ) => Promise<
        InfiniteQueryObserverResult<InfiniteData<PaginatedResponse<ReviewTemplate>, unknown>, Error>
    >;
};

/** The meeting plans the company's thematic meetings are built from, and the ways to open or add one. */
export type MeetingPlanSection = {
    templates: EditableMeetingTemplate[];
    isLoading: boolean;
    isError: boolean;
    onSelectTemplate: (templateId: string) => void;
    onCreateTemplate: () => void;
    onImportTemplate: () => void;
};

type ManageTemplatesProps = {
    evaluation360: ReviewTemplateSection;
    selfEvaluation: ReviewTemplateSection;
    managerEvaluation: ReviewTemplateSection;
    meetingPlans: MeetingPlanSection;
    templateCardActions: (reviewTemplate: ReviewTemplate) => OptionProps[];
    onStartBuilding: (type: EvaluationTypeValue) => void;
    onTemplateCardClick?: (templateId: string) => void;
};

type CollapsibleSectionRowProps = PropsWithChildren<{
    dataTestId: string;
    onToggleCollapse: () => void;
}>;

/**
 * A section of the page: a row inside the page's single card, carrying no surface of its own. Clicking
 * it anywhere folds or unfolds the section, which is why the heading inside stops the click it handles
 * itself and why its body keeps its own clicks.
 */
const CollapsibleSectionRow = ({
    dataTestId,
    onToggleCollapse,
    children,
}: CollapsibleSectionRowProps) => (
    <div
        className={'cursor-pointer'}
        onClick={onToggleCollapse}
        data-testid={dataTestId}
    >
        <Container gap={'md'}>{children}</Container>
    </div>
);

export const ManageTemplates = ({
    evaluation360,
    selfEvaluation,
    managerEvaluation,
    meetingPlans,
    templateCardActions,
    onStartBuilding,
    onTemplateCardClick,
}: ManageTemplatesProps) => {
    const { t } = useAmelioTranslations();

    const currentUser = useAtomValue(userInstanceAtom);
    const [is360Collapsed, setIs360Collapsed] = useAtom(is360CollapsedAtom);
    const [isManagerCollapsed, setIsManagerCollapsed] = useAtom(isManagerCollapsedAtom);
    const [isSelfCollapsed, setIsSelfCollapsed] = useAtom(isSelfCollapsedAtom);
    const [isMeetingPlanCollapsed, setIsMeetingPlanCollapsed] = useAtom(isMeetingPlanCollapsedAtom);

    const toggle360Collapsed = () => setIs360Collapsed((prev) => !prev);
    const toggleManagerCollapsed = () => setIsManagerCollapsed((prev) => !prev);
    const toggleSelfCollapsed = () => setIsSelfCollapsed((prev) => !prev);
    const toggleMeetingPlanCollapsed = () => setIsMeetingPlanCollapsed((prev) => !prev);

    return (
        <PageContainer>
            <TemplateHeader />

            <Container mt={'page'}>
                {/*
                 * The page's one and only card. The settings and every section are rows inside it, and
                 * the gap between them is declared once here, so a row a feature flag hides takes its
                 * spacing with it.
                 */}
                <Card
                    borderRadius={'3xl'}
                    padding={'lg'}
                    glass={'lg'}
                    dataTestId={'manage-templates-card'}
                >
                    <Container gap={'2xl'}>
                        {currentUser.isAdminUser && <TemplateSettings />}

                        <CollapsibleSectionRow
                            dataTestId={'manage-templates-360-tile'}
                            onToggleCollapse={toggle360Collapsed}
                        >
                            <TemplateSection
                                heading={t('templateManagement.template.review360')}
                                isCreatingTemplate={evaluation360.isCreatingTemplate}
                                isLoadingTemplates={evaluation360.isLoading}
                                hasMoreTemplates={evaluation360.hasMoreTemplates}
                                isFetchingNextPage={evaluation360.isFetchingNextPage}
                                companyTemplates={evaluation360.templates}
                                onImportTemplate={evaluation360.onImport}
                                onStartBuilding={() =>
                                    onStartBuilding(EvaluationType.EVALUATION_360)
                                }
                                onTemplateCardClick={onTemplateCardClick}
                                templateCardActions={templateCardActions}
                                loadMoreTemplates={evaluation360.fetchMore}
                                isCollapsed={is360Collapsed}
                                onToggleCollapse={toggle360Collapsed}
                            />
                        </CollapsibleSectionRow>

                        <CollapsibleSectionRow
                            dataTestId={'manage-templates-self-tile'}
                            onToggleCollapse={toggleSelfCollapsed}
                        >
                            <TemplateSection
                                heading={t('templateManagement.template.selfReview')}
                                isCreatingTemplate={selfEvaluation.isCreatingTemplate}
                                isLoadingTemplates={selfEvaluation.isLoading}
                                hasMoreTemplates={selfEvaluation.hasMoreTemplates}
                                isFetchingNextPage={selfEvaluation.isFetchingNextPage}
                                companyTemplates={selfEvaluation.templates}
                                onImportTemplate={selfEvaluation.onImport}
                                onStartBuilding={() =>
                                    onStartBuilding(EvaluationType.SELF_EVALUATION)
                                }
                                onTemplateCardClick={onTemplateCardClick}
                                templateCardActions={templateCardActions}
                                loadMoreTemplates={selfEvaluation.fetchMore}
                                isCollapsed={isSelfCollapsed}
                                onToggleCollapse={toggleSelfCollapsed}
                            />
                        </CollapsibleSectionRow>

                        <CollapsibleSectionRow
                            dataTestId={'manage-templates-manager-tile'}
                            onToggleCollapse={toggleManagerCollapsed}
                        >
                            <TemplateSection
                                heading={t('common.reviewType.managerEvaluation')}
                                isCreatingTemplate={managerEvaluation.isCreatingTemplate}
                                isLoadingTemplates={managerEvaluation.isLoading}
                                hasMoreTemplates={managerEvaluation.hasMoreTemplates}
                                isFetchingNextPage={managerEvaluation.isFetchingNextPage}
                                companyTemplates={managerEvaluation.templates}
                                onImportTemplate={managerEvaluation.onImport}
                                onStartBuilding={() =>
                                    onStartBuilding(EvaluationType.MANAGER_EVALUATION)
                                }
                                onTemplateCardClick={onTemplateCardClick}
                                templateCardActions={templateCardActions}
                                loadMoreTemplates={managerEvaluation.fetchMore}
                                isCollapsed={isManagerCollapsed}
                                onToggleCollapse={toggleManagerCollapsed}
                            />
                        </CollapsibleSectionRow>

                        <CollapsibleSectionRow
                            dataTestId={'manage-templates-meeting-plan-tile'}
                            onToggleCollapse={toggleMeetingPlanCollapsed}
                        >
                            <MeetingPlanTemplateSection
                                templates={meetingPlans.templates}
                                isLoading={meetingPlans.isLoading}
                                isError={meetingPlans.isError}
                                isCollapsed={isMeetingPlanCollapsed}
                                onToggleCollapse={toggleMeetingPlanCollapsed}
                                onSelectTemplate={meetingPlans.onSelectTemplate}
                                onCreateTemplate={meetingPlans.onCreateTemplate}
                                onImportTemplate={meetingPlans.onImportTemplate}
                            />
                        </CollapsibleSectionRow>
                    </Container>
                </Card>
            </Container>
        </PageContainer>
    );
};
